/**
 * BoardErrorBoundary
 *
 * Wraps ONLY the flight-board area (AutoSwipePanels + table).
 * Ads, ticker, and header are OUTSIDE this boundary.
 *
 * On error:
 *   - Renders a professional board-region fallback
 *   - Logs error + componentStack + timestamp
 *   - Increments rolling crash counters (5 min / 24 h) via boardHealthStore
 *   - After 10 s auto-recovers by clearing the error and requesting a board remount
 *     (unless stable-fallback mode is active)
 */

import React from 'react';
import { recordBoardCrash, isInStableFallback } from '../services/boardHealthStore.js';

const AUTO_RECOVER_MS = 10000; // 10 seconds

class BoardErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, errorMessage: '' };
    this._recoverTimer = null;
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, errorMessage: String(error?.message || error).slice(0, 300) };
  }

  componentDidCatch(error, errorInfo) {
    const ts = new Date().toISOString();
    const short = String(error?.message || error).slice(0, 200);

    console.error(
      `[BoardErrorBoundary] ${ts} | ${short}`,
      { stack: error?.stack, componentStack: errorInfo?.componentStack }
    );

    // Record in singleton store for heartbeat
    recordBoardCrash(short);

    // Schedule auto-recovery (unless in stable-fallback)
    this._scheduleRecovery();
  }

  componentWillUnmount() {
    if (this._recoverTimer) clearTimeout(this._recoverTimer);
  }

  _scheduleRecovery() {
    if (this._recoverTimer) clearTimeout(this._recoverTimer);

    this._recoverTimer = setTimeout(() => {
      if (isInStableFallback()) {
        // In stable-fallback mode — don't auto-recover, stay on fallback
        console.log('[BoardErrorBoundary] Stable-fallback mode active, deferring auto-recover');
        // Check again in 60s
        this._recoverTimer = setTimeout(() => this._scheduleRecovery(), 60000);
        return;
      }

      console.log('[BoardErrorBoundary] Auto-recovering board after error');
      this.setState({ hasError: false, errorMessage: '' });

      // Notify parent to bump board key (remount the board subtree)
      if (typeof this.props.onRecovery === 'function') {
        this.props.onRecovery();
      }
    }, AUTO_RECOVER_MS);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div
          style={{
            flex: 1,
            minWidth: 0,
            overflow: 'hidden',
            backgroundColor: '#000000',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            fontFamily: 'Arial, Helvetica, sans-serif',
            color: '#ffffff',
          }}
        >
          <div
            style={{
              width: 50,
              height: 50,
              border: '4px solid #333',
              borderTop: '4px solid #FFD200',
              borderRadius: '50%',
              animation: 'board-eb-spin 1s linear infinite',
              marginBottom: 30,
            }}
          />
          <div style={{ fontSize: 36, fontWeight: 500, color: '#FFD200', textAlign: 'center', marginBottom: 16 }}>
            Updating flight information&hellip;
          </div>
          <div style={{ fontSize: 20, color: '#888', textAlign: 'center' }}>
            Service will resume automatically
          </div>
          {/* Inline keyframes — avoids adding a separate stylesheet */}
          <style>{`@keyframes board-eb-spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
        </div>
      );
    }

    return this.props.children;
  }
}

export default BoardErrorBoundary;
